import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Plus, Settings2, Printer, Trash2, Receipt, Repeat, Layers, BadgeCheck, Copy, Landmark, RefreshCw, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatError, tablePath } from "@/lib/api";
import { useLiveRefresh } from "@/lib/realtime";
import { money, shortDate, fromPrevious, blankInvoice, prefillFromRecord, SORTS } from "@/lib/invoice";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import ConfirmDialog from "@/components/ConfirmDialog";
import DateField from "@/components/DateField";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { useAuth } from "@/context/AuthContext";
import InvoiceSettingsDialog from "@/components/invoices/InvoiceSettingsDialog";
import QboActions, { useQboStatus } from "@/components/invoices/QboActions";
import InvoiceScansCell from "@/components/invoices/InvoiceScansCell";
import DuplicateInvoiceDialog from "@/components/invoices/DuplicateInvoiceDialog";
import MarkPaidDialog from "@/components/MarkPaidDialog";

const STATUS = { paid: "text-emerald-400 bg-emerald-400/10", unpaid: "text-amber-400 bg-amber-400/10", draft: "text-muted-foreground bg-secondary" };

// Click-to-edit table cell (admins only). Text fields edit on blur/Enter; date fields
// use the date picker. Non-admins just see the value.
function EditableCell({ editable, value, display, type = "text", testId, onSave }) {
  const [editing, setEditing] = useState(false);
  const [val, setVal] = useState(value ?? "");
  useEffect(() => { if (!editing) setVal(value ?? ""); }, [value, editing]);

  if (!editable) return <span data-testid={testId}>{display ?? (value || "—")}</span>;
  if (!editing)
    return (
      <button type="button" onClick={() => setEditing(true)} title="Click to edit"
        className="text-left rounded px-1.5 -mx-1.5 py-0.5 hover:bg-primary/10 hover:ring-1 hover:ring-primary/30 transition-colors cursor-text"
        data-testid={testId}>{display ?? (value || <span className="text-muted-foreground">—</span>)}</button>
    );
  if (type === "date")
    return <DateField id={`edit-${testId}`} value={val} onChange={(v) => { setEditing(false); if ((v || "") !== (value || "")) onSave(v || ""); }} />;
  const commit = () => { setEditing(false); if ((val || "") !== (value || "")) onSave(val); };
  return (
    <Input autoFocus value={val} onChange={(e) => setVal(e.target.value)} onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); commit(); } if (e.key === "Escape") { setEditing(false); setVal(value ?? ""); } }}
      className="h-7 py-1 text-sm" data-testid={`${testId}-input`} />
  );
}

// Click-to-edit +Total (admins only): type the exact +Total amount for this invoice.
// There is no auto-calc; the amount entered here is what shows and prints. Blank = none.
function TotalEditCell({ editable, inv, i, onSave }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState("");
  const has = inv.plus_total !== null && inv.plus_total !== undefined && inv.plus_total !== "";
  useEffect(() => { if (open) setVal(has ? String(inv.plus_total) : ""); }, [open, inv]); // eslint-disable-line react-hooks/exhaustive-deps

  const display = has ? money(inv.grand_total) : <span className="text-muted-foreground">—</span>;
  if (!editable) return <span className="font-mono" data-testid={`inv-total-${i}`}>{display}</span>;

  const commit = () => {
    const pt = val.trim() === "" ? null : Number(val) || 0;
    setOpen(false);
    const cur = has ? Number(inv.plus_total) : null;
    if (pt !== cur) onSave(pt);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" title="Click to set +Total"
          className="font-mono text-left rounded px-1.5 -mx-1.5 py-0.5 hover:bg-primary/10 hover:ring-1 hover:ring-primary/30 transition-colors cursor-pointer"
          data-testid={`inv-total-${i}`}>{display}</button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 bg-card space-y-3" data-testid={`total-editor-${i}`}>
        <div className="space-y-1">
          <Label className="text-xs text-muted-foreground">+Total ($)</Label>
          <Input type="number" step="0.01" autoFocus placeholder="Leave blank for none" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && commit()} className="h-8" data-testid={`total-editor-amount-${i}`} />
        </div>
        <Button size="sm" className="w-full hover:bg-blue-600 hover:text-white" onClick={commit} data-testid={`total-editor-save-${i}`}>Save</Button>
      </PopoverContent>
    </Popover>
  );
}

export default function InvoiceList() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const [invoices, setInvoices] = useState(null);
  const [settings, setSettings] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [toDelete, setToDelete] = useState(null);
  const [filters, setFilters] = useState({ status: "all", service_from: "", service_to: "", q: "" });
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState(new Set());
  const [bulkDelete, setBulkDelete] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [refreshAllOpen, setRefreshAllOpen] = useState(false);
  const linkedIds = useMemo(() => (invoices || []).filter((x) => x.source?.record_id && x.source?.db_id && x.source?.table).map((x) => x.id), [invoices]);
  const qbo = useQboStatus(isAdmin);
  const [duplicating, setDuplicating] = useState(null);
  const [scanCounts, setScanCounts] = useState({});
  const [sortBy, setSortBy] = useState(() => localStorage.getItem("invoice-sort") || "service_desc");
  const shown = useMemo(() => (invoices ? [...invoices].sort((SORTS[sortBy] || SORTS.service_desc)[1]) : null), [invoices, sortBy]);
  const pickSort = (v) => { setSortBy(v); localStorage.setItem("invoice-sort", v); };
  const loadScanCounts = () => {
    if (!invoices?.length) return;
    api.post("/invoices/scan-counts", { ids: invoices.map((x) => x.id).slice(0, 1000) }).then((r) => setScanCounts(r.data)).catch(() => {});
  };
  useEffect(loadScanCounts, [invoices]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => setQuery(filters.q), 300);
    return () => clearTimeout(t);
  }, [filters.q]);

  const day = (v) => (v ? String(v).slice(0, 10) : undefined);
  const load = useCallback(() => {
    const params = { status: filters.status === "all" ? undefined : filters.status, service_from: day(filters.service_from), service_to: day(filters.service_to), q: query || undefined };
    return Promise.all([api.get("/invoices", { params }), api.get("/invoice-settings")])
      .then(([a, b]) => { setInvoices(a.data); setSettings(b.data); setSelected((s) => new Set(a.data.filter((x) => s.has(x.id)).map((x) => x.id))); })
      .catch((e) => toast.error(formatError(e)));
  }, [filters.status, filters.service_from, filters.service_to, query]);

  useEffect(() => { load(); }, [load]);
  useLiveRefresh(load);
  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const filtered = filters.status !== "all" || filters.service_from || filters.service_to || filters.q;
  const sum = (invoices || []).reduce((s, i) => s + (i.grand_total || 0), 0);
  const printAll = () => {
    const p = { status: filters.status === "all" ? "" : filters.status, service_from: day(filters.service_from) || "", service_to: day(filters.service_to) || "", q: query };
    const qs = new URLSearchParams(Object.entries(p).filter(([, v]) => v)).toString();
    window.open(`/invoices/print-list${qs ? `?${qs}` : ""}`, "_blank");
  };

  const allChecked = !!invoices?.length && selected.size === invoices.length;
  const toggle = (id) => setSelected((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleAll = () => setSelected(allChecked ? new Set() : new Set(invoices.map((x) => x.id)));
  const printSelected = () => {
    localStorage.setItem("print-selected-ids", JSON.stringify([...selected]));
    window.open("/invoices/print-list?selected=1", "_blank");
  };
  const [paying, setPaying] = useState(false);
  const markPaid = async (date, label, note) => {
    try {
      const { data } = await api.post("/invoices/bulk-paid", { ids: [...selected], date, payment_note: note || undefined });
      toast.success(`${data.updated} invoice${data.updated === 1 ? "" : "s"} marked paid on ${label} and moved to Paid Invoices`);
      setSelected(new Set());
      setPaying(false);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };
  const removeSelected = async () => {
    try {
      const { data } = await api.post("/invoices/bulk-delete", { ids: [...selected] });
      toast.success(`${data.deleted} invoice${data.deleted === 1 ? "" : "s"} deleted`);
      setSelected(new Set());
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
    setBulkDelete(false);
  };

  const repeat = (inv) => navigate("/invoices/new", { state: { prefill: fromPrevious(inv, blankInvoice(settings)) } });

  // Bulk refresh: re-pull equipment from each invoice's source record (reuses the fixed parser).
  const bulkRefreshEquipment = async (ids) => {
    setRefreshing(true);
    let done = 0, skipped = 0;
    for (const id of ids) {
      try {
        const { data: full } = await api.get(`/invoices/${id}`);
        const src = full.source || {};
        if (!src.db_id || !src.table || !src.record_id) { skipped++; continue; }
        const { data: rec } = await api.get(`${tablePath(src.db_id, src.table)}/records/${src.record_id}`);
        const fresh = prefillFromRecord(rec.record, rec.columns, src.db_id, src.table, settings);
        const body = {};
        Object.keys(full).forEach((k) => { if (!["id", "created_at", "updated_at", "grand_total", "batch_id"].includes(k)) body[k] = full[k]; });
        body.sticker_nos = fresh.sticker_nos; body.scales = fresh.scales; body.other_equipment = fresh.other_equipment; body.lifts = fresh.lifts;
        await api.put(`/invoices/${id}`, body);
        done++;
      } catch (e) { skipped++; }
    }
    setRefreshing(false);
    toast.success(`Refreshed equipment on ${done} invoice${done === 1 ? "" : "s"}${skipped ? ` · ${skipped} skipped (no linked record)` : ""}`);
    setSelected(new Set());
    load();
  };

  // Inline edit: fetch the full invoice, apply the single change, and save it back.
  const saveField = async (inv, apply) => {
    try {
      const { data: full } = await api.get(`/invoices/${inv.id}`);
      const body = {};
      Object.keys(full).forEach((k) => { if (!["id", "created_at", "updated_at", "grand_total", "batch_id"].includes(k)) body[k] = full[k]; });
      apply(body);
      await api.put(`/invoices/${inv.id}`, body);
      toast.success("Saved");
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
  };

  const remove = async () => {
    try {
      await api.delete(`/invoices/${toDelete.id}`);
      toast.success(`Invoice ${toDelete.number} deleted`);
      load();
    } catch (e) {
      toast.error(formatError(e));
    }
    setToDelete(null);
  };

  return (
    <div className="fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-mono uppercase tracking-widest text-cyan-400">Billing</p>
          <h1 className="text-3xl sm:text-4xl font-bold tracking-tight mt-2">Invoices</h1>
          <p className="text-sm text-muted-foreground mt-2">Service invoices in your Works form layout. Start one here, or open any record and choose "Create invoice".{isAdmin && " Click a facility, route, date, tech or total in the table to edit it."}</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" asChild className="hover:bg-secondary hover:text-foreground"><a href="/deposit-slip" target="_blank" rel="noreferrer" data-testid="deposit-slip-button"><Landmark size={16} /> Deposit slip</a></Button>
          {isAdmin && <Button variant="outline" onClick={() => setShowSettings(true)} data-testid="invoice-settings-button" className="hover:bg-secondary hover:text-foreground"><Settings2 size={16} /> Business details</Button>}
          <Button onClick={() => navigate("/invoices/new")} data-testid="new-invoice-button" className="hover:bg-blue-600 hover:text-white"><Plus size={16} /> New invoice</Button>
        </div>
      </div>

      <div className="mt-6 flex flex-wrap items-end gap-3" data-testid="invoice-filters">
        <div className="space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Status</p>
          <Select value={filters.status} onValueChange={(v) => setF("status", v)}>
            <SelectTrigger className="w-32" data-testid="filter-status-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              {["all", "unpaid", "draft"].map((s) => <SelectItem key={s} value={s} className="capitalize" data-testid={`filter-status-${s}`}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1 w-44">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Service date from</p>
          <DateField id="filter-service-from" value={filters.service_from} onChange={(v) => setF("service_from", v)} />
        </div>
        <div className="space-y-1 w-44">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Service date to</p>
          <DateField id="filter-service-to" value={filters.service_to} onChange={(v) => setF("service_to", v)} />
        </div>
        <div className="space-y-1">
          <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Sort by</p>
          <Select value={sortBy} onValueChange={pickSort}>
            <SelectTrigger className="w-48" data-testid="invoice-sort-select"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-card">
              {Object.entries(SORTS).map(([k, [label]]) => <SelectItem key={k} value={k} data-testid={`invoice-sort-${k}`}>{label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <Input value={filters.q} onChange={(e) => setF("q", e.target.value)} placeholder="Search all fields: invoice #, facility, address, phone, notes, route, amount, date…" className="w-72" data-testid="filter-search-input" />
        <Button variant="outline" disabled={!invoices?.length} data-testid="print-all-button" className="hover:bg-secondary hover:text-foreground"
          onClick={printAll}>
          <Printer size={16} /> Print all ({invoices?.length || 0})
        </Button>
        {isAdmin && !!linkedIds.length && (
          <Button variant="outline" onClick={() => setRefreshAllOpen(true)} disabled={refreshing} data-testid="refresh-all-linked-button" className="hover:bg-secondary hover:text-foreground">
            {refreshing ? <Loader2 size={16} className="animate-spin" /> : <RefreshCw size={16} />} Refresh all linked ({linkedIds.length})
          </Button>
        )}
        {filtered && <Button variant="ghost" onClick={() => setFilters({ status: "all", service_from: "", service_to: "", q: "" })} data-testid="filter-clear-button">Clear</Button>}
        <p className="ml-auto text-sm text-muted-foreground" data-testid="invoice-summary">
          {invoices ? `${invoices.length} invoice${invoices.length === 1 ? "" : "s"} · ${money(sum)}` : ""}
        </p>
      </div>

      {selected.size > 0 && (
        <div className="mt-4 flex flex-wrap items-center gap-3 rounded-lg border border-primary/40 bg-primary/10 px-4 py-2" data-testid="invoice-selection-bar">
          <p className="text-sm font-medium" data-testid="invoice-selected-count">{selected.size} selected</p>
          <Button size="sm" variant="outline" onClick={printSelected} data-testid="print-selected-button" className="hover:bg-secondary hover:text-foreground"><Printer size={15} /> Print selected</Button>
          <Button size="sm" variant="outline" onClick={() => setPaying(true)} data-testid="mark-paid-selected-button" className="text-emerald-400 hover:bg-emerald-500/10 hover:text-emerald-400"><BadgeCheck size={15} /> Mark paid</Button>
          {isAdmin && <Button size="sm" variant="outline" onClick={() => bulkRefreshEquipment([...selected])} disabled={refreshing} data-testid="bulk-refresh-equipment-button" className="hover:bg-secondary hover:text-foreground">{refreshing ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />} Refresh equipment</Button>}
          {isAdmin && qbo?.connected && <QboActions ids={[...selected]} onDone={load} />}
          {isAdmin && <Button size="sm" variant="outline" onClick={() => setBulkDelete(true)} data-testid="delete-selected-button" className="text-red-400 hover:bg-red-500/10 hover:text-red-400"><Trash2 size={15} /> Delete selected</Button>}
          <Button size="sm" variant="ghost" onClick={() => setSelected(new Set())} data-testid="clear-selection-button">Clear selection</Button>
        </div>
      )}

      <div className="mt-4 border rounded-lg bg-card overflow-x-auto" data-testid="invoice-list">
        <table className="w-full text-sm">
          <thead className="bg-secondary text-left">
            <tr><th className="pl-4 py-2.5 w-8"><Checkbox className="data-[state=indeterminate]:bg-primary/40" checked={allChecked ? true : selected.size ? "indeterminate" : false} onCheckedChange={toggleAll} disabled={!invoices?.length} data-testid="invoice-select-all" aria-label="Select all" /></th>{["Invoice #", "Facility", "Route", "Service date", "Tech", "Status", "Date Pd", "+Total", "Scans", ""].map((h) => <th key={h} className="px-4 py-2.5 font-medium whitespace-nowrap">{h}</th>)}</tr>
          </thead>
          <tbody>
            {shown?.map((inv, i) => (
              <tr key={inv.id} className="border-t hover:bg-secondary/40 transition-colors" data-testid={`invoice-row-${i}`}>
                <td className="pl-4 py-2.5"><Checkbox checked={selected.has(inv.id)} onCheckedChange={() => toggle(inv.id)} data-testid={`invoice-select-${i}`} aria-label={`Select invoice ${inv.number}`} /></td>
                <td className="px-4 py-2.5 font-mono"><Link to={`/invoices/${inv.id}`} className="text-primary hover:underline" data-testid={`invoice-link-${i}`}>{inv.number}</Link></td>
                <td className="px-4 py-2.5"><EditableCell editable={isAdmin} value={inv.customer?.name} testId={`inv-facility-${i}`} onSave={(v) => saveField(inv, (b) => { b.customer = { ...(b.customer || {}), name: v }; })} /></td>
                <td className="px-4 py-2.5 font-mono text-xs"><EditableCell editable={isAdmin} value={inv.route} testId={`invoice-route-${i}`} onSave={(v) => saveField(inv, (b) => { b.route = v; })} /></td>
                <td className="px-4 py-2.5 whitespace-nowrap"><EditableCell editable={isAdmin} type="date" value={day(inv.service_date)} display={shortDate(inv.service_date)} testId={`inv-service-date-${i}`} onSave={(v) => saveField(inv, (b) => { b.service_date = v || null; })} /></td>
                <td className="px-4 py-2.5"><EditableCell editable={isAdmin} value={inv.tech} testId={`inv-tech-${i}`} onSave={(v) => saveField(inv, (b) => { b.tech = v; })} /></td>
                <td className="px-4 py-2.5"><span className={`px-2 py-0.5 rounded text-xs capitalize ${STATUS[inv.status]}`}>{inv.status}</span>
                  {inv.qbo?.invoice_id && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-emerald-600/20 text-emerald-400" title={`In QuickBooks${inv.qbo.synced_at ? ` · sent ${shortDate(inv.qbo.synced_at)}` : ""}`} data-testid={`invoice-qbo-badge-${i}`}>QB</span>}
                  {inv.qbo?.error && <span className="ml-1.5 px-1.5 py-0.5 rounded text-[10px] font-bold bg-red-500/15 text-red-400" title={inv.qbo.error} data-testid={`invoice-qbo-error-${i}`}>QB!</span>}</td>
                <td className="px-4 py-2.5 whitespace-nowrap"><EditableCell editable={isAdmin} type="date" value={day(inv.date_paid)} display={inv.date_paid ? shortDate(inv.date_paid) : undefined} testId={`inv-date-paid-${i}`} onSave={(v) => saveField(inv, (b) => { b.date_paid = v || null; })} /></td>
                <td className="px-4 py-2.5"><TotalEditCell editable={isAdmin} inv={inv} i={i} onSave={(pt) => saveField(inv, (b) => { b.plus_total = pt; })} /></td>
                <td className="px-4 py-2.5"><InvoiceScansCell inv={inv} i={i} count={scanCounts[inv.id]} onAdded={loadScanCounts} /></td>
                <td className="px-4 py-2.5">
                  <div className="flex gap-3 justify-end">
                    <button onClick={() => setDuplicating(inv)} className="text-muted-foreground hover:text-cyan-400" data-testid={`invoice-duplicate-${i}`} aria-label="Duplicate" title="Duplicate with a new invoice number"><Copy size={15} /></button>
                    <button onClick={() => repeat(inv)} className="text-muted-foreground hover:text-cyan-400" data-testid={`invoice-repeat-${i}`} aria-label="Repeat this visit" title="New invoice from this visit"><Repeat size={15} /></button>
                    {inv.batch_id ? <a href={`/invoices/batch/${inv.batch_id}/print`} target="_blank" rel="noreferrer" className="text-muted-foreground hover:text-primary" data-testid={`invoice-batch-print-${i}`} title="Print the whole batch"><Layers size={15} /></a> : <span className="w-[15px]" aria-hidden="true" />}
                    <Link to={`/invoices/${inv.id}/print`} target="_blank" className="text-muted-foreground hover:text-primary" data-testid={`invoice-print-${i}`} aria-label="Print"><Printer size={15} /></Link>
                    {isAdmin && <button onClick={() => setToDelete(inv)} className="text-muted-foreground hover:text-red-400" data-testid={`invoice-delete-${i}`} aria-label="Delete"><Trash2 size={15} /></button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {invoices?.length === 0 && (
          <div className="p-12 text-center text-muted-foreground" data-testid="invoice-empty">
            <Receipt className="mx-auto mb-3 text-primary" /> No invoices yet.
          </div>
        )}
      </div>
      {settings && <InvoiceSettingsDialog open={showSettings} settings={settings} onClose={() => setShowSettings(false)} onSaved={setSettings} />}
      <ConfirmDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)} onConfirm={remove}
        title="Delete this invoice?" description={`Invoice ${toDelete?.number} will be permanently removed.`} testId="delete-invoice" />
      <ConfirmDialog open={bulkDelete} onOpenChange={setBulkDelete} onConfirm={removeSelected}
        title={`Delete ${selected.size} invoice${selected.size === 1 ? "" : "s"}?`} description="The selected invoices will be permanently removed." testId="bulk-delete-invoice" />
      <DuplicateInvoiceDialog inv={duplicating} onClose={() => setDuplicating(null)} />
      <ConfirmDialog open={refreshAllOpen} onOpenChange={setRefreshAllOpen} onConfirm={() => { setRefreshAllOpen(false); bulkRefreshEquipment(linkedIds); }}
        title={`Refresh equipment on ${linkedIds.length} invoice${linkedIds.length === 1 ? "" : "s"}?`}
        description="Equipment (scales, lifts, other) on every record-linked invoice below will be re-pulled from its source record, overwriting any manual equipment edits. Other fields are untouched." testId="refresh-all-linked" confirmLabel="Refresh equipment" confirmClass="bg-blue-600 hover:bg-blue-700 hover:text-white" />
      <MarkPaidDialog open={paying} count={selected.size} showNote onClose={() => setPaying(false)} onConfirm={markPaid} />
    </div>
  );
}
